import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function ServicesPage() {
  const services = [
    {
      title: 'MRI Equipment Leasing',
      description: 'High-field and mid-field mobile MRI units available on flexible lease terms. We deliver, install, and maintain the equipment — your licensed clinical team operates it.',
      image: 'https://public.readdy.ai/ai/img_res/c7b56b11c70bb2496723e5a2680b4aac.jpg',
      features: [
        'High-field (1.5T, 3.0T) and mid-field MRI systems available',
        'Short-term, long-term, and emergency lease options',
        'Ideal for overflow capacity, renovations, and equipment failures',
        'Complete site preparation and power infrastructure coordination',
        'Full DICOM connectivity and PACS integration support',
        'Preventative maintenance and uptime monitoring included'
      ],
      applications: [
        'Capacity expansion',
        'Renovation coverage',
        'Equipment failure backup',
        'Disaster recovery',
        'Overflow management'
      ]
    },
    {
      title: 'CT Equipment Leasing',
      description: 'Multi-slice CT units for hospitals, trauma centers, and outpatient facilities. We provide the equipment and infrastructure — your team handles all clinical operations.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20mobile%20CT%20scanner%20trailer%20exterior%20showing%20advanced%20multi-slice%20computed%20tomography%20equipment%20on%20wheels%20at%20hospital%20facility%20with%20clean%20professional%20medical%20imaging%20hardware%20and%20institutional%20background&width=1200&height=800&seq=lease-detail-ct-001&orientation=landscape',
      features: [
        '16-slice, 64-slice, and 128-slice CT systems',
        'Rapid deployment within 48–72 hours',
        'Complete delivery, positioning, and power setup',
        'Equipment maintenance and technical support included',
        'Flexible lease durations from days to multi-year',
        'Site readiness consultation provided'
      ],
      applications: [
        'Trauma center backup',
        'Outpatient expansion',
        'Emergency department support',
        'Construction interim coverage',
        'Capacity overflow'
      ]
    },
    {
      title: 'Ultrasound Equipment Leasing',
      description: 'Portable and mobile ultrasound units for clinics and hospitals. Equipment-only leasing — your clinical staff performs all imaging procedures.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20portable%20mobile%20ultrasound%20equipment%20unit%20in%20clinical%20hospital%20setting%20showing%20modern%20diagnostic%20imaging%20hardware%20for%20medical%20applications%20with%20clean%20healthcare%20environment%20and%20simple%20neutral%20background&width=1200&height=800&seq=lease-detail-ultrasound-001&orientation=landscape',
      features: [
        'High-resolution portable ultrasound systems',
        'General, cardiac, and vascular imaging hardware',
        'Clinic and hospital-based deployment options',
        'Flexible rental and leasing terms',
        'Technical support and maintenance included',
        'Rapid delivery and setup coordination'
      ],
      applications: [
        'Clinic capacity expansion',
        'Hospital overflow support',
        'Mobile health programs',
        'Equipment upgrade transition',
        'Interim coverage'
      ]
    },
    {
      title: 'X-Ray Equipment Leasing',
      description: 'Digital mobile X-ray systems for hospitals and specialty care facilities. We provide and maintain the equipment — your radiologic technologists operate it.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20digital%20mobile%20X-ray%20equipment%20system%20in%20professional%20healthcare%20facility%20showing%20advanced%20radiography%20hardware%20for%20diagnostic%20imaging%20with%20clean%20clinical%20environment%20and%20simple%20neutral%20background&width=1200&height=800&seq=lease-detail-xray-001&orientation=landscape',
      features: [
        'Digital radiography (DR) systems',
        'Portable and mobile configurations available',
        'Immediate PACS integration support',
        'Low-dose radiation technology',
        'Comprehensive equipment maintenance',
        'Flexible short and long-term leases'
      ],
      applications: [
        'Hospital bedside imaging support',
        'Emergency department backup',
        'Orthopedic facility coverage',
        'Long-term care facilities',
        'Post-operative support'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <main className="pt-24 sm:pt-32 pb-12 sm:pb-20">
        {/* Page Header */}
        <section className="px-4 sm:px-6 mb-12 sm:mb-20">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-blue text-[13px] uppercase tracking-[2px] font-medium mb-3 sm:mb-4">
              EQUIPMENT LEASING
            </p>
            <h1 className="text-navy text-[30px] sm:text-[40px] lg:text-[48px] font-semibold leading-tight mb-4 sm:mb-6">
              Medical Imaging Equipment for Lease
            </h1>
            <p className="text-[#5A6C7D] text-base sm:text-lg leading-relaxed max-w-3xl mx-auto">
              Mediline Medical provides mobile MRI, CT, ultrasound, and X-ray equipment on flexible lease terms. We deliver, install, and maintain the hardware. Your licensed clinical team operates it.
            </p>
          </div>
        </section>

        {/* Operational Responsibility Disclaimer */}
        <section className="px-4 sm:px-6 mb-12 sm:mb-16">
          <div className="max-w-4xl mx-auto bg-navy/5 border-l-4 border-navy rounded-r-xl p-6 sm:p-8">
            <h2 className="text-navy text-lg font-semibold mb-3 uppercase tracking-wide">Operational Responsibility</h2>
            <p className="text-[#5A6C7D] text-sm sm:text-base leading-relaxed mb-3">
              <strong className="text-navy">The Company provides medical imaging equipment only.</strong> All clinical services — including but not limited to staffing, scheduling, patient care, billing, and compliance — are the sole responsibility of the healthcare provider.
            </p>
            <p className="text-[#5A6C7D] text-sm sm:text-base leading-relaxed">
              The Company does not provide radiology services, technologists, or clinical personnel of any kind.
            </p>
          </div>
        </section>

        {/* Services Detail */}
        {services.map((service, index) => (
          <section
            key={index}
            className={`px-4 sm:px-6 py-12 sm:py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F7F9FB]'}`}
          >
            <div className="max-w-7xl mx-auto">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                {/* Image */}
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="rounded-xl sm:rounded-2xl overflow-hidden h-[220px] sm:h-[320px] lg:h-[400px]">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <h2 className="text-navy text-[24px] sm:text-[30px] lg:text-[36px] font-semibold mb-3 sm:mb-4">
                    {service.title}
                  </h2>
                  <p className="text-[#5A6C7D] text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                    {service.description}
                  </p>

                  {/* Features */}
                  <div className="mb-6 sm:mb-8">
                    <h3 className="text-navy text-lg sm:text-xl font-semibold mb-3 sm:mb-4">What&apos;s Included in the Lease</h3>
                    <ul className="space-y-2 sm:space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 sm:gap-3">
                          <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                            <i className="ri-check-line text-slate-blue text-lg"></i>
                          </div>
                          <span className="text-[#5A6C7D] text-sm sm:text-[15px]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Applications */}
                  <div>
                    <h3 className="text-navy text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Common Use Cases</h3>
                    <div className="flex flex-wrap gap-2">
                      {service.applications.map((app, idx) => (
                        <span
                          key={idx}
                          className="bg-slate-blue/10 text-slate-blue px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium"
                        >
                          {app}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* CTA Section */}
        <section className="px-4 sm:px-6 mt-12 sm:mt-20">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-navy to-navy-light rounded-2xl sm:rounded-3xl p-8 sm:p-12 lg:p-16 text-center">
            <h2 className="text-white text-[24px] sm:text-[30px] lg:text-[36px] font-semibold mb-3 sm:mb-4">
              Need a Custom Lease Arrangement?
            </h2>
            <p className="text-white/85 text-base sm:text-lg mb-6 sm:mb-8">
              Our leasing team can structure a contract tailored to your facility&apos;s timeline and equipment requirements.
            </p>
            <a
              href="/contact"
              className="inline-block bg-white text-navy px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base font-semibold hover:shadow-2xl transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Contact Our Leasing Team
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
